import _ from "lodash";
import { fitoutPeriodDays } from "lib/fitoutPeriod";
import classNames from "classnames";
import Datasource, { DatasourceResponse } from "lib/datasource";
import LocalNotification from "lib/Notification";
import moment from "moment";
import Point from "./Point";
import React, { memo, useEffect, useReducer, useState } from "react";
import Reschedule from "./Reschedule";
import Step from "./Step";
import Steps from "./Steps";
import LeaseSummary from "./LeaseSummary";
import Summary from "./Summary";
import validate from "validate.js";
import { Board, Button } from "v2/components/shared";
import { Confirmation } from "v2/components/Projects";
import { Error as ErrorState, LoadingState, View } from "components/Shared";
import { NewProjectContext } from "v2/context";
import { parse as parseUrl } from "query-string";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Select as Lease } from "v2/components/Leases";
import { Select as Template } from "v2/components/Templates";
import { Select as Tenancy } from "v2/components/Tenancies";
import { Select as Tenant } from "v2/components/Tenants";
import { toSentenceSerial } from "underscore.string";
import { translate } from "lib";
import { v4 as uuidv4 } from "uuid";
import axios from "v2/utils/axios";

const constraints = {
	property: {
		presence: { message: "^Must select a Property", allowEmpty: false }
	},
	template: {
		presence: { message: "^Must select a Template", allowEmpty: false }
	}
} as any;

const SESSION_STORAGE_KEY = "cached_new_project";

const reducer = (state: any, action: any) => {
	switch (action.type) {
		case "SET_TENANT":
			return { tenant: action.value, project: { ...state.project, name: _.get(action.value, ["trading_name"]) } };
		case "SET_PROJECT":
			return { ...state, project: action.value };
		default:
			return state;
	}
};

const useGetTemplate = (template_id: string) => {
	const [template, setTemplate] = useState<any>(null);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "template", renderTrigger: renderTrigger }));
	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setTemplate(response.normalizedMainModelResponse)
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(template_id)) datasource.get("v2", `templates/${template_id}`, {});
	}, [datasource, template_id]);

	return [template, setTemplate];
};

/**
 * Fetch a pending Project (status: "pending") to pre-populate the wizard when the
 * user continues from the Pending Projects list via `/projects/:id/schedule`
 * (api-contract §5.2/§7, react-frontend-tasks F3).
 *
 * Also surfaces a fetch failure (e.g. a 403 if `ProjectPolicy#show?` denies the
 * requesting property admin, a 404, or a network error) so the caller can render an
 * error state instead of hanging on `<LoadingState />` forever — see code review round 1.
 */
const useGetPendingProject = (id?: string) => {
	const [pendingProject, setPendingProject] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "project", renderTrigger: renderTrigger }));
	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success === false) {
				setError({ response: { status: response.errorStatus } });
			} else {
				setPendingProject(response.normalizedMainModelResponse);
			}
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		// `template` is the only one of these the Summary step reads; the units and
		// the tenant now come from the project's own flat `tenancy_ids` / `tenant_id`
		// attributes (see Summary's docblock — the included relationships are empty
		// on an amendment project). The rest are still asked for because the
		// Scheduling and Project steps gate `clickable` on their presence.
		if (!_.isEmpty(id))
			datasource.get("v2", `projects/${id}`, {
				queryStringParameters: {
					include: JSON.stringify(["template", "tenancy", "tenant", "lease"])
				}
			});
	}, [datasource, id]);

	return { pendingProject, error };
};

const Create = ({ history, location, match }: RouteComponentProps<{ id?: string }>) => {
	const { template_id } = parseUrl(location.search) as { template_id: string };
	const pendingId = _.get(match, ["params", "id"]);
	const isFinalizeMode = !_.isEmpty(pendingId);
	// New-project mode (`/projects/new`) keeps the original editable wizard: open on the
	// Template step (index 0), or skip straight to Tenancy (index 1) when a `template_id`
	// query param preselects the template. Finalize mode (`/projects/:id/schedule`) opens
	// on the read-only Summary step (index 0) and the pending-project effect below jumps
	// it to Scheduling (index 1).
	const [current, setCurrent] = useState(!isFinalizeMode && !!template_id ? 1 : 0);
	// The editable new-project wizard's Tenancy step. Finalize mode does NOT read
	// this to display the units — it passes `pendingProject.tenancy_ids` straight
	// to Summary — but it is still set below, because the Scheduling and Project
	// steps gate `clickable` on it.
	const [tenancy, setTenancy] = useState<any>(null);
	const [lease, setLease] = useState<any>(null);
	const [viewState, setViewState] = useState(-1);
	// NOTE (scope flag, react-frontend-tasks F5): `setDeadlines` was previously called
	// from the now-removed editable Lease step's `onUpdate` handler to derive key-date
	// deadlines from the entered lease dates for new-project (`/projects/new`) mode.
	// With that step removed, `deadlines` stays `[]` for new-project submissions. This
	// is a product-visible regression scoped out of this feature (Summary is read-only
	// by design); flagging for a follow-up decision rather than silently expanding scope.
	const [deadlines, setDeadlines] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [rescheduledTasks, setRescheduledTasks] = useState([] as any[]);
	const [template, setTemplate] = useGetTemplate(template_id);
	// NEW-PROJECT MODE ONLY, now that the Summary has no Lease section: the
	// editable Tenancy step sets it and the editable Lease step reads it for its
	// per-sqm rent calculation. Finalize mode no longer seeds it — the amendment
	// owns the commercial terms and they are shown on the lease, not here.
	const [tenancyArea, setTenancyArea] = useState<number | undefined>();
	const [state, dispatch] = useReducer(reducer, {
		tenant: null,
		project: { kind: "", name: "", reference: "", portfolio_ids: [] }
	});

	const { tenant, project } = state;
	const setTenant = (tenant: any) => dispatch({ type: "SET_TENANT", value: tenant });
	const setProject = (project: any) => dispatch({ type: "SET_PROJECT", value: project });

	// Finalize mode (continuing a pending project): pre-populate the read-only
	// "Summary" step (index 0) so the user lands there, reviews it, and advances to
	// "Scheduling" via the Next button. We intentionally do NOT auto-jump to
	// Scheduling: doing so mounted the Summary and immediately unmounted it, tearing
	// down the Tenancy Card's in-flight address/building fetches and mapbox init
	// before they could complete (which left the map and address list blank).
	const { pendingProject, error: pendingProjectError } = useGetPendingProject(isFinalizeMode ? pendingId : undefined);
	useEffect(() => {
		if (!!pendingProject) {
			setTemplate(pendingProject.template);
			// NOT for display any more — Summary is given `tenancy_ids` / `tenant_id`
			// and fetches each record itself (see its docblock). These three are set
			// only so the Scheduling and Project steps stay clickable, which is what
			// they gate on. They arrive as id-only stubs on an amendment project,
			// which is exactly why nothing reads their fields.
			setTenancy(pendingProject.tenancy);
			setLease(pendingProject.lease);
			setTenant(pendingProject.tenant);
			setProject({
				kind: pendingProject.kind || "",
				name: pendingProject.name || "",
				reference: pendingProject.reference || "",
				portfolio_ids: pendingProject.portfolio_ids || []
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingProject]);

	// A PENDING PROJECT'S WIZARD BELONGS TO ITS OWNER. Sim, 18 Sep 2026.
	//
	// Anyone who could merely SEE the project used to land here: a plain team
	// member (ProjectPolicy#show? passes on their project Permission row) and any
	// property admin on the property. Both got the full wizard, set dates on work
	// that is not theirs to schedule, and — for the team member — only found out
	// at the very end, when the finalize PATCH came back 403.
	//
	// The rule is now the same one `Project.pending_for` uses for the queue that
	// links here: the project-owner PARTY, and nobody else. No admin bypass, no
	// property-admin allowance — see ProjectPolicy#finalize?, which refuses the
	// PATCH on the same predicate, so this redirect is UX and not the enforcement.
	//
	// `replace`, not `push`: a push leaves the wizard in the history, so Back off
	// the dashboard would bounce straight back into it.
	//
	// ONLY for a pending project. `is_project_owner` is on every project, but a
	// finalized one does not reach this screen through the wizard at all, and a
	// non-owner opening a stale /schedule URL for a project that has since gone
	// live should land on it (Projects/Show), not be bounced to the dashboard.
	// The handover date shown on the Scheduling step. Finalize mode reads the
	// pending project's own `handover_at`; new-project mode has no project yet, so
	// it falls back to whatever the wizard holds. Either may be absent, and the
	// caller must render nothing rather than format it — see the note there.
	const handoverAt = _.get(pendingProject, "handover_at") || _.get(lease, "handover_at");
	// The fitout period shown beside it, in calendar days. Same two sources and
	// the same order: a /lease_data project has no `lease`, and its FitOutTerm
	// lives on the amendment — the wizard read only the lease and printed "0".
	// `_.isNil`, not truthiness: a stated period of 0 is a fact, not a gap.
	const pendingFitoutPeriod = _.get(pendingProject, "fitout_period");
	const fitoutPeriod = !_.isNil(pendingFitoutPeriod) ? pendingFitoutPeriod : fitoutPeriodDays(lease);
	// The amendment's "Opening Date" (Project#opening_at), shown as "Expected Open
	// for Trade Date" under the handover date. Same two sources, same order as above.
	const openingAt = _.get(pendingProject, "opening_at") || _.get(lease, "opening_at");
	const pendingProjectStatus = _.get(pendingProject, "status");
	const isPendingProjectOwner = !!_.get(pendingProject, "is_project_owner");
	const deniedPendingProject = pendingProjectStatus === "pending" && !isPendingProjectOwner;
	useEffect(() => {
		if (deniedPendingProject) history.replace("/");
	}, [deniedPendingProject, history]);

	const onSubmit = () => {
		setLoading(true);
		for(var i = 0; i < rescheduledTasks.length; i++){
			rescheduledTasks[i].due_at_fixed = true;
		}

		// Finalize mode: PATCH the existing pending project instead of creating a new
		// one (api-contract §6.3). Lease/Tenant/Tenancy already exist (D7) — only the
		// rescheduled tasks and the pending -> finalized status transition are sent.
		if (isFinalizeMode) {
			axios
				.patch(`${process.env.REACT_APP_API_ENDPOINT_V2}projects/${pendingId}/finalize`, {
					rescheduled_tasks: JSON.stringify(rescheduledTasks)
				})
				.then(() => {
					new LocalNotification("Project Created");
					sessionStorage.setItem(SESSION_STORAGE_KEY, "");
					history.push(`/projects/${pendingId}`);
				})
				.catch(() => {
					new LocalNotification("Something Went Wrong", "Something Went Wrong", "error");
					setLoading(false);
				});
			return;
		}
		const payload = {
			template: {
				id: template.id
			},
			tenancy: {
				id: _.get(tenancy, "id"),
				floor_id: _.get(tenancy, "floor.id", null) || _.get(tenancy, "floor_id", ""),
				building_id: _.get(tenancy, "building.id", null) || _.get(tenancy, "building_id", ""),
				name: _.get(tenancy, "name", ""),
				area: _.get(tenancy, "area", ""),
				division: _.get(tenancy, "division", ""),
				status: _.get(tenancy, "status", ""),
				kind: _.get(tenancy, "status", "")
			},
			tenant: {
				name: _.get(tenant, "name"),
				trading_name: _.get(tenant, "trading_name"),
				contact_details: _.get(tenant, "contact_details"),
				contact_name: _.get(tenant, "contact_name"),
				contact_email: _.get(tenant, "contact_email"),
				sector: _.get(tenant, "sector"),
				source: _.get(tenant, "source"),
				phone_number: _.get(tenant, "phone_number", "").replace(/[^0-9]/g, ""),
				full_time_equivalent: parseInt(_.get(tenant, "full_time_equivalent", 0), 10),
				business_code_id: _.get(tenant, "kind.id"),
				addresses: _.get(tenant, "addresses", [])
			},
			company: {
				name: _.get(tenant, "name")
			},
			property: {
				id: _.get(tenancy, ["property", "id"]),
				name: _.get(tenancy, ["property", "name"])
			},
			lease: {
				annual_review: _.get(lease, "annual_review", ""),
				base_rent_cents: !!_.get(lease, "base_rent_cents", 0) ? parseInt(_.get(lease, "base_rent_cents", 0), 10) : 0,
				base_rent_frequency: _.get(lease, "base_rent_frequency", ""),
				duration: _.get(lease, "duration", ""),
				ends_at: !!_.get(lease, "ends_at", false) && moment(_.get(lease, "ends_at", false)).format("YYYY-MM-DD"),
				fitout_contribution_cents: _.get(lease, "fitout_contribution_cents", 0),
				fitout_ends_at:
					!!_.get(lease, "fitout_ends_at", false) && moment(_.get(lease, "fitout_ends_at", false)).format("YYYY-MM-DD"),
				handover_at:
					!!_.get(lease, "handover_at", false) && moment(_.get(lease, "handover_at", false)).format("YYYY-MM-DD"),
				incentives: _.get(lease, "incentives", ""),
				kind: _.get(lease, "kind", ""),
				lease_renewals: parseInt(_.get(lease, "lease_renewals", 0), 10),
				net_lettable_area: _.get(lease, "net_lettable_area", ""),
				opening_at:
					!!_.get(lease, "opening_at", false) && moment(_.get(lease, "opening_at", false)).format("YYYY-MM-DD"),
				operational_outgoings_cents: parseInt(_.get(lease, "operational_outgoings_cents", 0), 10),
				permitted_use: _.get(lease, "permitted_use", ""),
				rent_per_sqm_cents: parseInt(_.get(lease, "rent_per_sqm_cents", 0), 10),
				rent_review_percent: parseInt(_.get(lease, "rent_review_percent", 0), 10),
				rent_starts_at:
					!!_.get(lease, "rent_starts_at", false) && moment(_.get(lease, "rent_starts_at", false)).format("YYYY-MM-DD"),
				seating_area: _.get(lease, "seating_area", ""),
				starts_at: !!_.get(lease, "starts_at", false) && moment(_.get(lease, "starts_at", false)).format("YYYY-MM-DD"),
				status: _.get(lease, "status", ""),
				statutory_outgoings_cents: parseInt(_.get(lease, "statutory_outgoings_cents", 0), 10),
				storage_area: parseInt(_.get(lease, "storage_area", 0), 10),
				vacant_possession_at:
					!!_.get(lease, "vacant_possession_at", false) &&
					moment(_.get(lease, "vacant_possession_at", false)).format("YYYY-MM-DD")
			},
			project: {
				...project,
				deadlines: _.map(deadlines, deadline => ({
					name: _.get(deadline, "name"),
					date: _.get(deadline, "date"),
					task_template_id: _.get(deadline, "task_id")
				})),
				template_id: template.id
			},
			rescheduled_tasks: JSON.stringify(rescheduledTasks)
		};
		let { property } = tenancy;
		if (template!.kind === "retail" && !!property.name) {
			constraints.tenant = {
				presence: { allowEmpty: false }
			};
			constraints.tenancy = {
				presence: { allowEmpty: false }
			};
		}
		const errors = validate(
			{
				template,
				tenant,
				tenancy,
				lease,
				property
			},
			constraints
		);

		if (errors !== undefined) {
			let errorMessage = toSentenceSerial(_.map(_.keys(errors)));
			new LocalNotification(`but ${errorMessage}`, translate("error_boundary_body"), "error");
			setLoading(false);
		} else {
			axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}projects`, { project: payload })
				.then(response => {
					new LocalNotification("Project Created");
					sessionStorage.setItem(SESSION_STORAGE_KEY, "");
					history.push(`/projects/${_.get(response.data, "data.id")}`);
				})
				.catch(() => {
					new LocalNotification("Something Went Wrong", "Something Went Wrong", "error");
					setLoading(false);
				});
		}
	};

	// Surface a fetch failure (e.g. a 403 while the backend authorization fix for property
	// admins viewing pending projects is rolled out, a 404, or a network error) instead of
	// hanging on <LoadingState /> forever.
	if (isFinalizeMode && pendingProjectError) return <ErrorState resource={pendingProjectError} />;
	if (isFinalizeMode && !pendingProject) return <LoadingState />;
	// Effects run AFTER the render that scheduled them, so without this the
	// wizard — Summary, the tenancy, the whole schedule — paints for one frame
	// before the redirect above fires. Same reason Projects/Show holds a
	// LoadingState over its pending redirect.
	if (deniedPendingProject) return <LoadingState />;

	return (
		<NewProjectContext.Provider value={{ template }}>
			<Board className="lg:w-full lg:mt-7">
				<div className="flex-1">
					<Steps setCurrent={setCurrent} current={current} className="px-5 lg:mb-20">
						{isFinalizeMode ? (
							// Finalize a pending project: the Template/Tenancy/Tenant/Lease already
							// exist, so show them as read-only steps instead of the editable ones.
							// TWO of them, then the shared Scheduling and Project steps:
							// 1 Summary · 2 Lease · 3 Scheduling · 4 Project.
							// `toArray` (v2/utils) flattens this fragment, so both Steps are
							// numbered and navigable exactly as if they were direct children.
							<>
							<Step title="Summary" className="mt-6 mx-auto" clickable>
								<View resource="project" hideTitle>
									{/* IDS, NOT RECORDS (Sim, 28 Aug). `tenancy_ids` and `tenant_id` are
									    flattened onto ProjectSerializer precisely because the included
									    relationships cannot be resolved by the normalizer — and on an
									    AMENDMENT project they are the only place the units and the tenant
									    appear at all, since both answer from the Yardi side
									    (`Project#tenancies` -> the amendment's snapshot, `Project#tenant`
									    -> `lease_agreement.current_tenant`) rather than through TCP's
									    Lease. Summary hands them to the same shared tabs the project
									    workspace uses. No `lease` — see Summary's docblock. */}
									<Summary
										template={template}
										tenancyIds={_.get(pendingProject, "tenancy_ids")}
										tenantId={_.get(pendingProject, "tenant_id")}
										propertyId={_.get(pendingProject, ["property_summary", "id"])}
									/>
									<Button
										label="Next"
										className={classNames(
											"float-right text-white mt-5 bg-blue-100 hover:shadow-hover shadow-button"
										)}
										onClick={() => setCurrent(current + 1)}
									/>
								</View>
							</Step>
							{/* THE LEASE ITSELF — the Yardi `LeaseAgreement` and the `Amendment`
							    that own this project's commercial terms. Ids again, not records:
							    `lease_agreement_id` and `amendment_id` are flat on
							    ProjectSerializer, and `amendment_id` here is the uuid FK, not the
							    human-readable `yardi_amendment_id`. LeaseSummary fetches both.
							    Always clickable, like Summary: nothing is entered on either, so
							    there is no prerequisite to gate on. */}
							<Step title="Lease" className="mt-6 mx-auto" clickable>
								<View resource="project" hideTitle>
									<LeaseSummary
										leaseAgreementId={_.get(pendingProject, "lease_agreement_id")}
										amendmentId={_.get(pendingProject, "amendment_id")}
									/>
									<Button
										label="Next"
										className={classNames(
											"float-right text-white mt-5 bg-blue-100 hover:shadow-hover shadow-button"
										)}
										onClick={() => setCurrent(current + 1)}
									/>
								</View>
							</Step>
							</>
						) : (
							// New-project mode (`/projects/new`): the original editable steps.
							<>
								<Step title="Template" className="mt-6 mx-auto" clickable>
									<View
										resource="template"
										className={classNames({
											"form-group": !template,
											card: !!template
										})}
										hideTitle
									>
										<Template onUpdate={setTemplate} template={template} />
										<Button
											label="< Reset"
											className={classNames("float-left text-black-60 mt-5", { hidden: !template })}
											onClick={() => setTemplate(null)}
										/>
										<Button
											label="Confirm"
											className={classNames("float-right text-white mt-5", {
												"bg-blue-100 hover:shadow-hover shadow-button": true,
												hidden: !template
											})}
											onClick={() => {
												setCurrent(current + 1);
												setViewState(-1);
											}}
										/>
									</View>
								</Step>
								<Step title="Tenancy" className="mt-6 mx-auto" clickable={!!template || !!tenancy}>
									<View
										resource="tenancy"
										className={classNames({
											"form-group": !tenancy,
											card: !!tenancy
										})}
										hideTitle
									>
										<Tenancy
											tenancy={tenancy}
											setTenancyArea={setTenancyArea}
											onUpdate={setTenancy}
											viewState={viewState}
											setViewState={setViewState}
										/>
										<Button
											label="< Reset"
											className={classNames("float-left text-black-60 mt-5", { hidden: viewState !== 1 })}
											onClick={() => setViewState(0)}
										/>
										<Button
											label="Confirm"
											className={classNames("float-right text-white mt-5", {
												"bg-blue-100 hover:shadow-hover shadow-button": true,
												hidden: viewState !== 1
											})}
											onClick={() => {
												setCurrent(current + 1);
												setViewState(-1);
											}}
										/>
									</View>
								</Step>
								<Step title="Tenant" className="mt-6 mx-auto" clickable={(!!tenancy && !!template) || !!tenant}>
									<View resource="tenant" className={"form-group"} hideTitle>
										<Tenant tenant={tenant} onUpdate={setTenant} viewState={viewState} setViewState={setViewState} />
										<Button
											label="< Reset"
											className={classNames("float-left text-black-60 mt-5", { hidden: viewState !== 1 })}
											onClick={() => setViewState(0)}
										/>
										<Button
											label="Confirm"
											className={classNames("float-right text-white mt-5", {
												"bg-blue-100 hover:shadow-hover shadow-button": true,
												hidden: viewState !== 1
											})}
											onClick={() => {
												setCurrent(current + 1);
												setViewState(-1);
											}}
										/>
									</View>
								</Step>
								<Step
									title="Lease"
									className="mt-6 mx-auto"
									clickable={true || (!!tenancy && !!template && !!tenant) || !!lease}
								>
									<View resource="lease" className={"form-group"} hideTitle>
										<Lease
											lease={lease}
											onUpdate={(lease: any) => {
												_.map(
													["ends_at", "fitout_ends_at", "handover_at", "opening_at", "rent_starts_at", "starts_at"],
													(d: any) => {
														if (!!lease && !!lease[d]) {
															deadlines.push({
																id: uuidv4(),
																date: moment(lease[d]).format("YYYY-MM-DD"),
																name: translate(d)
															});
														}
													}
												);
												!!lease ? setDeadlines(_.sortBy(deadlines, "date")) : setDeadlines([]);
												setLease(lease);
											}}
											viewState={viewState}
											setViewState={setViewState}
											tenancyArea={tenancyArea}
										/>
										<Button
											label="< Reset"
											className={classNames("float-left text-black-60 mt-5", { hidden: viewState !== 1 })}
											onClick={() => setViewState(0)}
										/>
										<Button
											label="Confirm"
											className={classNames("float-right text-white mt-5", {
												"bg-blue-100 hover:shadow-hover shadow-button": true,
												hidden: viewState !== 1
											})}
											onClick={() => {
												setCurrent(current + 1);
												setViewState(-1);
											}}
										/>
									</View>
								</Step>
							</>
						)}
						<Step
							title="Scheduling"
							className="mt-6 mx-auto"
							clickable={!!tenancy && !!template && !!tenant && !!lease}
						>
							<View resource="task" hideTitle>
								<div className="border rounded-1 bg-blue-darker  text-white text-14 p-4 font-bold">
									Calendar Days and Scheduled Due Date are calculated based on the design of your workflow
									template. You can make adjustments by clicking the Calendar Days to complete and/or Adjusted Due Date data
									to ensure your project reflects real life due dates. <br />
									<br />
									You can also reschedule your workflow backwards from one task. To do this, click the{" "}
									<span className="italic">"I would like to reverse schedule from a Task"</span> button, adjust the due
									date for the Task that you wish to reschedule backwards from (eg Handover). When this task’s adjusted
									due at or adjusted calendar days is completed its parent tasks will also be recalculated. Once you've
									completed the first reschedule backwards, unclick the box, then you can continue to finalise your
									program.
								</div>
								<div className="border rounded-1 border-blue-darker text-18 leading-6 p-4 font-medium mt-3 flex justify-between items-center">
									<div>
										<div>
											{/*
												THE AMENDMENT'S MoveInDate. Sim, 18 Sep 2026.

												This read `moment(_.get(lease, "handover_at"))`, TCP's
												legacy `Lease` record — which a /lease_data project does
												not have. `_.get` returned undefined, and
												`moment(undefined)` is NOT an invalid date, it is NOW: the
												wizard printed TODAY as the handover date, every day, on
												the one screen where that number decides whether someone
												reverse-schedules from Handover.

												`handover_at` comes off the project (Project#handover_at →
												amendments.move_in_date). THE LINE ALWAYS RENDERS: with no
												MoveInDate it reads "Unknown", the same word the project
												header uses for a missing datapoint. It is never dropped —
												a person scheduling from Handover has to see that there is
												no handover date, not find the line missing — and it is
												never a date nobody stated.
											*/}
											<span>
												Handover Date:{" "}
												{!!handoverAt ? (
													moment(handoverAt).format("DD MMM YYYY")
												) : (
													<span className="muted">{translate("unknown")}</span>
												)}
											</span>
											{/* ALWAYS RENDERS, like the handover date beside it: someone
											    reverse-scheduling has to see that there is no fitout period,
											    not find the line missing. Yardi's FitOutTerm is the period —
											    see lib/fitoutPeriod. */}
											<span className="ml-7">
												Fitout Period:{" "}
												{!_.isNil(fitoutPeriod) ? (
													`${fitoutPeriod} (Calendar Days)`
												) : (
													<span className="muted">{translate("unknown")}</span>
												)}
											</span>
										</div>
										{/* ITS OWN ROW, under Handover Date (Sim, 28 Oct 2026). ALWAYS
										    RENDERS, like the two above. The amendment's "Opening Date" —
										    Project#opening_at. */}
										<div className="mt-2">
											Expected Open for Trade Date:{" "}
											{!!openingAt ? (
												moment(openingAt).format("DD MMM YYYY")
											) : (
												<span className="muted">{translate("unknown")}</span>
											)}
										</div>
									</div>

									<span>
										<div className="text-14">
											<Point className="text-error-darker mr-3" />
											Rescheduling of a task has created an adjusted due date to be in the past or the adjusted calendar
											days is 0
										</div>
										<span className="text-14">
											<Point className="text-warning-light mr-3" />
											Rescheduling of a parent task has adjusted this task's due date
										</span>
									</span>
								</div>
								<Reschedule templateId={template?.id} onUpdate={setRescheduledTasks} />
								<Button
									label="< Previous"
									className={classNames("float-left text-black-60 mt-5", { hidden: !rescheduledTasks })}
									onClick={() => {
										setRescheduledTasks([]);
										setCurrent(c => c - 1);
									}}
								/>
								<Button
									label="Next"
									className={classNames("float-right text-white mt-5", {
										"bg-blue-100 hover:shadow-hover shadow-button": true,
										hidden: !rescheduledTasks
									})}
									onClick={() => setCurrent(current + 1)}
								/>
							</View>
						</Step>
						<Step title="Project" className="mt-6 mx-auto" clickable={!!tenancy && !!template && !!tenant && !!lease}>
							<View resource="project" hideTitle>
								{!!loading ? (
									<LoadingState />
								) : (
									<Confirmation project={project} onUpdate={setProject} onSubmit={onSubmit} />
								)}
							</View>
						</Step>
					</Steps>
				</div>
			</Board>
		</NewProjectContext.Provider>
	);
};

export default memo(withRouter(Create));
